// Types
import { IIndexable } from "types/common";

// Images
import quickSwapIcon from "assets/images/defi/quickswap.png";
import balancerIcon from "assets/images/defi/balancer.svg";
import balancerDarkIcon from "assets/images/defi/balancer-dark.svg";
import marketIcon from "assets/images/defi/market.png";
import qiDaoIcon from "assets/images/defi/qiDao.png";
import beefyFinanceIcon from "assets/images/defi/beefy-finance.png";
import autoFarmIcon from "assets/images/defi/autofarm.svg";
import meshswapIcon from "assets/images/defi/meshswap.svg";
import harvestIcon from "assets/images/defi/harvest.svg";
import usdcIcon from "assets/images/defi/usdcIcon.png";
import usdcDarkIcon from "assets/images/defi/usdc-dark.png";
import daiIcon from "assets/images/defi/dai.png";
import daiDarkIcon from "assets/images/defi/dai-dark.png";
import deltaIcon from "assets/images/defi/delta.svg";
import dystopiaIcon from "assets/images/defi/dystopia.svg";
import ovixIcon from "assets/images/defi/0vix.png";
import aaveIcon from "assets/images/defi/aave.svg";
import oliveIcon from "assets/images/defi/olive.png";
import cianIcon from "assets/images/defi/cian.png";

// Config
import { currentNetworkInfo } from "config/network";

export const defiIcons: IIndexable = {
  quickswap: {
    light: quickSwapIcon,
    dark: quickSwapIcon,
  },
  balancer: {
    light: balancerIcon,
    dark: balancerDarkIcon,
  },
  meshswap: {
    light: meshswapIcon,
    dark: meshswapIcon,
  },
  qidao: {
    light: qiDaoIcon,
    dark: qiDaoIcon,
  },
  market: {
    light: marketIcon,
    dark: marketIcon,
  },
  beefy: {
    light: beefyFinanceIcon,
    dark: beefyFinanceIcon,
  },
  autofarm: {
    light: autoFarmIcon,
    dark: autoFarmIcon,
  },
  harvest: {
    light: harvestIcon,
    dark: harvestIcon,
  },
  delta: {
    light: deltaIcon,
    dark: deltaIcon,
  },
  dystopia: {
    light: dystopiaIcon,
    dark: dystopiaIcon,
  },
  ovix: {
    light: ovixIcon,
    dark: ovixIcon,
  },
  aave: {
    light: aaveIcon,
    dark: aaveIcon,
  },
  olive: {
    light: oliveIcon,
    dark: oliveIcon,
  },
  cian: {
    light: cianIcon,
    dark: cianIcon,
  },
};

export const defiLabels: IIndexable = {
  quickswap: "Quickswap",
  balancer: "Balancer",
  meshswap: "Meshswap",
  meshswap2: "Meshswap",
  meshswap3: "Meshswap",
  qidao: "QiDao",
  market: "Market",
  beefy: "Beefy Finance",
  beefyBalancer: "Beefy",
  beefyDystopia: "Beefy",
  autofarm: "AutoFarm",
  harvest: "Harvest",
  delta: "delta.theta",
  dystopia: "Dystopia",
  ovix: "0vix",
  aave: "AAVE",
  olive: "Olive",
  cian: "CIAN",
};

export const enum defiTypes {
  SD_DEX = "sd_dex",
  DEX = "dex",
  YIELD_OPTIMIZER = "yo",
  LENDING_BORROWING = "lb",
  OPTION_TRADING = "ot",
  LEVERAGE_STAKING = "ls",
}

export const defiTypeLabels: IIndexable = {
  [defiTypes.SD_DEX]: "SD pools",
  [defiTypes.DEX]: "Liquidity pools",
  [defiTypes.YIELD_OPTIMIZER]: "Yield optimizers",
  [defiTypes.LENDING_BORROWING]: "Lending & borrowing",
  [defiTypes.OPTION_TRADING]: "Options trading & vaults",
  [defiTypes.LEVERAGE_STAKING]: "Automated leverage staking",
};

export const defiDescriptions: IIndexable = {
  [defiTypes.SD_DEX]:
    "SD is the governance token for Stader. Liquidity providers in these pools receive a portion of the swap fees, SD rewards and additional incentives",
  [defiTypes.DEX]:
    "Liquidity pools are pools of tokens that let you swap one token with another. Liquidity providers in the pools get rewarded with a part of the swap fees and additional incentives.",
  [defiTypes.LENDING_BORROWING]:
    "Lending and Borrowing markets provide a reward for lending tokens and also allow borrowing of tokens against collateral for a fee.",
  [defiTypes.YIELD_OPTIMIZER]:
    "Reward optimizers boost returns from reward farms by autocompounding the rewards.",
  [defiTypes.OPTION_TRADING]:
    "Options Trading protocols and Defi Option Vaults enable you to buy and sell 'Call' and 'Put' options manually or participate in a vault that automates it.",
  [defiTypes.LEVERAGE_STAKING]:
    "Leveraged staking is the process of borrowing Matic using MaticX as collateral and then staking it and repeating the loop.",
};

export const defiTypeHasAPYMapping: IIndexable = {
  sd_dex: false,
  dex: false,
  yo: true,
  lb: false,
  ot: false,
  ls: true,
};

export const defiPrimaryURLKey: IIndexable = {
  sd_dex: "addLiquidity",
  dex: "addLiquidity",
  yo: "supply",
  lb: "deposit",
  ot: "deposit",
};

export const defiTypeMapping: IIndexable = {
  quickswap: defiTypes.DEX,
  meshswap: defiTypes.DEX,
  meshswap2: defiTypes.DEX,
  balancer: defiTypes.DEX,
  sdbalancer: defiTypes.SD_DEX,
  dystopia: defiTypes.DEX,
  market: defiTypes.LENDING_BORROWING,
  meshswap3: defiTypes.LENDING_BORROWING,
  ovix: defiTypes.LENDING_BORROWING,
  aave: defiTypes.LENDING_BORROWING,
  harvest: defiTypes.YIELD_OPTIMIZER,
  beefy: defiTypes.YIELD_OPTIMIZER,
  beefyBalancer: defiTypes.YIELD_OPTIMIZER,
  beefyDystopia: defiTypes.YIELD_OPTIMIZER,
  autofarm: defiTypes.YIELD_OPTIMIZER,
  delta: defiTypes.OPTION_TRADING,
  olive: defiTypes.OPTION_TRADING,
  cian: defiTypes.LEVERAGE_STAKING,
};

export const defiUrls: IIndexable = {
  quickswap: {
    addLiquidity:
      "https://quickswap.exchange/#/pools?currency0=ETH&currency1=0xfa68FB4628DFF1028CFEc22b4162FCcd0d45efb6",
    swap: "https://quickswap.exchange/#/swap?currency1=0xfa68FB4628DFF1028CFEc22b4162FCcd0d45efb6",
  },
  sd_quickswap: {
    addLiquidity:
      "https://quickswap.exchange/#/add/0x1d734a02ef1e1f5886e66b0673b71af5b53ffa94/0x2791bca1f2de4661ed88a30c99a7a9449aa84174/v3",
    swap: "https://quickswap.exchange/#/swap?swapIndex=2&currency0=ETH&currency1=0x1d734A02eF1e1f5886e66b0673b71Af5B53ffA94",
  },
  meshswap1: {
    addLiquidity:
      "https://meshswap.fi/exchange/pool/detail/0x9458e0d6fe48e411d84073ab7f58cc2631c3fefd",
    swap: "https://meshswap.fi/exchange/swap?outputCurrency=0xfa68FB4628DFF1028CFEc22b4162FCcd0d45efb6",
  },
  meshswap2: {
    addLiquidity:
      "https://meshswap.fi/exchange/pool/detail/0x9895319f0ade21f09e2b1af42b0decb7992fda33",
    swap: "https://meshswap.fi/exchange/swap?outputCurrency=0xfa68FB4628DFF1028CFEc22b4162FCcd0d45efb6",
  },
  balancer: {
    addLiquidity:
      "https://polygon.balancer.fi/#/pool/0xb20fc01d21a50d2c734c4a1262b4404d41fa7bf000000000000000000000075c",
    swap: "https://polygon.balancer.fi/#/trade",
  },
  sd_balancer: {
    addLiquidity:
      "https://app.balancer.fi/#/polygon/pool/0x4973f591784d9c94052a6c3ebd553fcd37bb0e5500020000000000000000087f",
    swap: "https://polygon.balancer.fi/#/trade",
  },
  dystopia: {
    addLiquidity:
      "https://www.dystopia.exchange/liquidity/0xadb2395fec4d096b5086d3daf8b33f9f7568ba25",
    swap: "https://www.dystopia.exchange/swap",
  },
  market: {
    supply: "https://polygon.market.xyz/pool/9",
    borrow: "https://polygon.market.xyz/pool/9",
  },
  meshswap3: {
    supply: "https://meshswap.fi/single/pool",
    borrow: "https://meshswap.fi/single/pool",
  },
  ovix: {
    supply: "https://app.0vix.com/",
    borrow: "https://app.0vix.com/",
    get primary() {
      return this.supply;
    },
  },
  aave: {
    supply:
      "https://app.aave.com/reserve-overview/?underlyingAsset=0xfa68fb4628dff1028cfec22b4162fccd0d45efb6&marketName=proto_polygon_v3",
    borrow:
      "https://app.aave.com/reserve-overview/?underlyingAsset=0xfa68fb4628dff1028cfec22b4162fccd0d45efb6&marketName=proto_polygon_v3",
  },
  harvest: {
    deposit: "https://app.harvest.finance/",
  },
  beefy: {
    deposit: "https://app.beefy.finance/#/polygon/vault/quick-wmatic-maticx",
  },
  beefyBalancer: {
    deposit: "https://app.beefy.com/vault/balancer-matic-maticx-v2",
  },
  beefyDystopia: {
    deposit: "https://app.beefy.finance/vault/dystopia-wmatic-maticx",
  },
  autofarm: {
    deposit: "https://autofarm.network/polygon/194/",
  },
  delta: {
    deposit: "https://app.deltatheta.tech/terminal/MaticX-DAI",
  },
  olive: {
    deposit: "https://oliveapp.finance/earn/vaults/maticx-covered-call-polygon",
  },
  cian: {
    deposit: "https://dapp.cian.app/strategy/matic/create",
  },
};

export const itemGroups = [
  {
    type: defiTypes.LEVERAGE_STAKING,
    title: defiTypeLabels[defiTypes.LEVERAGE_STAKING],
    hasAPY: defiTypeHasAPYMapping[defiTypes.LEVERAGE_STAKING],
    items: [
      {
        id: "cian",
        name: defiLabels["cian"],
        platformIcon: defiIcons["cian"],
        type: defiTypeMapping["cian"],
        icon: currentNetworkInfo.icon,
        token: "Matic",
        links: defiUrls["cian"],
        showIcon: false,
      },
    ],
  },
  {
    type: defiTypes.DEX,
    title: defiTypeLabels[defiTypes.DEX],
    hasAPY: defiTypeHasAPYMapping[defiTypes.DEX],
    items: [
      {
        id: "balancer",
        name: defiLabels["balancer"],
        platformIcon: defiIcons["balancer"],
        type: defiTypeMapping["balancer"],
        icon: currentNetworkInfo.icon,
        token: "Matic",
        links: defiUrls["balancer"],
      },
      {
        id: "quickswap",
        name: defiLabels["quickswap"],
        platformIcon: defiIcons["quickswap"],
        type: defiTypeMapping["quickswap"],
        icon: currentNetworkInfo.icon,
        token: "Matic",
        links: defiUrls["quickswap"],
      },
      {
        id: "meshswap1",
        name: defiLabels["meshswap"],
        platformIcon: defiIcons["meshswap"],
        type: defiTypeMapping["meshswap"],
        icon: currentNetworkInfo.icon,
        token: "Matic",
        links: defiUrls["meshswap1"],
      },
      {
        id: "meshswap2",
        name: defiLabels["meshswap2"],
        platformIcon: defiIcons["meshswap"],
        type: defiTypeMapping["meshswap2"],
        icon: {
          light: usdcIcon,
          dark: usdcDarkIcon,
        },
        token: "USDC",
        links: defiUrls["meshswap2"],
      },
      {
        id: "dystopia",
        name: defiLabels["dystopia"],
        platformIcon: defiIcons["dystopia"],
        type: defiTypeMapping["dystopia"],
        icon: currentNetworkInfo.icon,
        token: "Matic",
        links: defiUrls["dystopia"],
      },
    ],
  },
  {
    type: defiTypes.SD_DEX,
    title: defiTypeLabels[defiTypes.SD_DEX],
    hasAPY: defiTypeHasAPYMapping[defiTypes.SD_DEX],
    isSDPool: true,
    items: [
      {
        id: "sd_balancer",
        name: defiLabels["balancer"],
        platformIcon: defiIcons["balancer"],
        type: defiTypeMapping["balancer"],
        icon: currentNetworkInfo.tokenXIcon,
        token: "MaticX",
        links: defiUrls["sd_balancer"],
      },
      {
        id: "sd_quickswap",
        name: defiLabels["quickswap"],
        platformIcon: defiIcons["quickswap"],
        type: defiTypeMapping["quickswap"],
        icon: {
          light: usdcIcon,
          dark: usdcDarkIcon,
        },
        token: "USDC",
        links: defiUrls["sd_quickswap"],
      },
    ],
  },
  {
    type: defiTypes.LENDING_BORROWING,
    title: defiTypeLabels[defiTypes.LENDING_BORROWING],
    hasAPY: defiTypeHasAPYMapping[defiTypes.LENDING_BORROWING],
    items: [
      {
        id: "ovix",
        name: defiLabels["ovix"],
        platformIcon: defiIcons["ovix"],
        type: defiTypeMapping["ovix"],
        icon: currentNetworkInfo.icon,
        token: "Matic",
        links: defiUrls["ovix"],
      },
      {
        id: "meshswap3",
        name: defiLabels["meshswap3"],
        platformIcon: defiIcons["meshswap"],
        type: defiTypeMapping["meshswap3"],
        icon: currentNetworkInfo.icon,
        token: "Matic",
        links: defiUrls["meshswap3"],
      },
      // {
      //   id: "market",
      //   name: defiLabels["market"],
      //   platformIcon: defiIcons["market"],
      //   type: defiTypeMapping["market"],
      //   icon: tokenIcon,
      //   token: "Matic",
      //   links: defiUrls["market"],
      // },
      {
        id: "aave",
        name: defiLabels["aave"],
        platformIcon: defiIcons["aave"],
        type: defiTypeMapping["aave"],
        icon: currentNetworkInfo.icon,
        token: "Matic",
        links: defiUrls["aave"],
      },
    ],
  },
  {
    type: defiTypes.YIELD_OPTIMIZER,
    title: defiTypeLabels[defiTypes.YIELD_OPTIMIZER],
    hasAPY: defiTypeHasAPYMapping[defiTypes.YIELD_OPTIMIZER],
    items: [
      {
        id: "beefyBalancer",
        name: defiLabels["beefyBalancer"],
        platformIcon: defiIcons["beefy"],
        type: defiTypeMapping["beefyBalancer"],
        icon: currentNetworkInfo.icon,
        token: "Matic",
        links: defiUrls["beefyBalancer"],
        pair: {
          name: "Balancer",
          icon: {
            light: balancerIcon,
            dark: balancerDarkIcon,
          },
        },
      },
      // {
      //   id: "beefy",
      //   name: defiLabels["beefy"],
      //   platformIcon: defiIcons["beefy"],
      //   type: defiTypeMapping["beefy"],
      //   icon: tokenIcon,
      //   token: "Matic",
      //   links: defiUrls["beefy"],
      //   pair: {
      //     name: "Quickswap",
      //     icon: {
      //       light: quickSwapIcon,
      //       dark: quickSwapIcon,
      //     },
      //   },
      // },
      // {
      //   id: "harvest",
      //   name: defiLabels["harvest"],
      //   platformIcon: defiIcons["harvest"],
      //   type: defiTypeMapping["harvest"],
      //   icon: tokenIcon,
      //   token: "Matic",
      //   links: defiUrls["harvest"],
      // },
      // {
      //   id: "beefyDystopia",
      //   name: defiLabels["beefyDystopia"],
      //   platformIcon: defiIcons["beefy"],
      //   type: defiTypeMapping["beefyDystopia"],
      //   icon: tokenIcon,
      //   token: "Matic",
      //   links: defiUrls["beefyDystopia"],
      //   pair: {
      //     name: "Dystopia",
      //     icon: {
      //       light: dystopiaIcon,
      //       dark: dystopiaIcon,
      //     },
      //   },
      // },
      // {
      //   id: "autofarm",
      //   name: defiLabels["autofarm"],
      //   platformIcon: defiIcons["autofarm"],
      //   type: "yo",
      //   icon: tokenIcon,
      //   token: "Matic",
      //   links: defiUrls["autofarm"],
      // },
    ],
  },
  {
    type: defiTypes.OPTION_TRADING,
    title: defiTypeLabels[defiTypes.OPTION_TRADING],
    hasAPY: defiTypeHasAPYMapping[defiTypes.OPTION_TRADING],
    items: [
      {
        id: "olive",
        name: defiLabels["olive"],
        platformIcon: defiIcons["olive"],
        type: defiTypeMapping["olive"],
        showIcon: false,
        links: defiUrls["olive"],
        hasAPY: true,
      },
      {
        id: "delta",
        name: defiLabels["delta"],
        platformIcon: defiIcons["delta"],
        type: defiTypeMapping["delta"],
        icon: {
          light: daiIcon,
          dark: daiDarkIcon,
        },
        token: "DAI",
        links: defiUrls["delta"],
      },
    ],
  },
];
